<template>
  <div class="relative min-h-screen w-full bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="container mx-auto px-4 sm:px-8 py-8">
      <nav class="text-sm text-gray-600 mb-6 flex flex-wrap">
        <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
        <span class="mx-1">/</span>
        <router-link to="/career-fair-management" class="hover:text-blue-700 font-medium">Career Fair</router-link>
        <span class="mx-1">/</span>
        <span class="text-blue-900 font-semibold">{{ fair ? fair.year : "..." }}</span>
      </nav>

      <div v-if="loading" class="h-40 bg-white rounded-2xl animate-pulse"></div>
      <div v-else-if="!fair" class="bg-white rounded-2xl p-10 text-center text-slate-500">Career fair not found.</div>
      <div v-else>
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 class="text-3xl font-extrabold text-heading m-0">{{ fair.title || ("Career Fair " + fair.year) }}</h1>
            <p class="text-slate-500 mt-2 mb-0">
              Preview:
              <router-link :to="'/career-fair/' + fair.year + '/preview'" class="text-twelve font-semibold" target="_blank">
                /career-fair/{{ fair.year }}/preview
              </router-link>
              <span class="mx-2">·</span>
              Public:
              <span :class="fair.is_published ? 'text-green-700 font-semibold' : 'text-slate-400'">
                /career-fair/{{ fair.year }} {{ fair.is_published ? "" : "(unpublished)" }}
              </span>
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              v-if="hasPermission('career-fair-publish')"
              type="button"
              class="px-4 py-2 text-sm font-bold border-0 rounded-lg cursor-pointer"
              :class="fair.is_published ? 'bg-gray-200 text-heading' : 'bg-green-600 text-white'"
              @click="togglePublish"
            >
              {{ fair.is_published ? "Unpublish" : "Publish" }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside class="lg:col-span-3">
            <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <p class="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 border-b border-gray-100 m-0">Sections</p>
              <button
                v-for="section in sectionNav"
                :key="section.key"
                type="button"
                class="w-full text-left px-4 py-3 text-sm border-0 border-b border-gray-100 cursor-pointer"
                :class="activeSection === section.key ? 'bg-primary-100 text-primary font-bold' : 'bg-white text-heading'"
                @click="selectSection(section)"
              >
                <span>{{ section.title }}</span>
                <span v-if="!section.ready" class="block text-xs text-slate-400 font-normal mt-1">Coming next</span>
              </button>
            </div>
          </aside>
          <div class="lg:col-span-9">
            <HeroSectionEditor
              v-if="activeSection === 'hero'"
              :career-fair-id="fair.id"
              :hero="fair.hero"
              @saved="onHeroSaved"
            />
            <div v-else class="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center">
              <p class="text-lg font-bold text-heading mb-2">{{ activeMeta.title }}</p>
              <p class="text-slate-500 m-0">This section will be wired in the next pass. Hero is available now.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import HeroSectionEditor from "./components/HeroSectionEditor.vue";
import { CAREER_FAIR_FIELDS, cloneApolloData } from "./graphql";

const SHOW_QUERY = gql`
  query CareerFair($id: ID!) {
    careerFair(id: $id) {
      error
      message
      data { ${CAREER_FAIR_FIELDS} }
    }
  }
`;

const PUBLISH_MUTATION = gql`
  mutation PublishCareerFair($id: ID!, $publish: Boolean!) {
    publishCareerFair(id: $id, publish: $publish) {
      error
      message
      data { id is_published }
    }
  }
`;

export default {
  name: "CareerFairManagementEdit",
  components: { HeroSectionEditor },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      fair: null,
      activeSection: "hero",
      sectionNav: [
        { key: "hero", title: "Hero", ready: true },
        { key: "event_details", title: "Event details", ready: false },
        { key: "nav", title: "Quick-nav cards", ready: false },
        { key: "sponsors", title: "Sponsors", ready: false },
        { key: "companies", title: "Participating companies", ready: false },
        { key: "visit_us", title: "Visit Us", ready: false },
        { key: "exhibit", title: "Exhibit With Us", ready: false },
        { key: "getting_ready", title: "Getting Ready", ready: false },
        { key: "jobs", title: "Jobs", ready: false },
        { key: "campus", title: "Coming to Campus", ready: false },
        { key: "faqs", title: "FAQs", ready: false },
        { key: "contact", title: "Contact Us", ready: false },
      ],
    };
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    activeMeta() {
      var self = this;
      var found = this.sectionNav.find(function (s) {
        return s.key === self.activeSection;
      });
      return found || this.sectionNav[0];
    },
  },
  created() {
    this.load();
  },
  methods: {
    hasPermission(name) {
      if (this.role === "superadmin") return true;
      var permissions = this.$store.getters.permissions || "";
      return String(permissions).split(",").indexOf(name) !== -1;
    },
    selectSection(section) {
      this.activeSection = section.key;
    },
    onHeroSaved(hero) {
      if (!this.fair || !hero) return;
      this.fair = Object.assign({}, this.fair, {
        hero: cloneApolloData(hero),
      });
    },
    async load() {
      this.loading = true;
      try {
        var result = await this.$apollo.query({
          query: SHOW_QUERY,
          variables: { id: this.id || this.$route.params.id },
          fetchPolicy: "network-only",
        });
        var payload = result.data && result.data.careerFair;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Career Fair", text: payload.message });
        } else {
          this.fair = payload && payload.data ? cloneApolloData(payload.data) : null;
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Could not load career fair." });
      } finally {
        this.loading = false;
      }
    },
    async togglePublish() {
      if (!this.fair) return;
      var publish = !this.fair.is_published;
      var confirm = await this.$swal.fire({
        title: publish ? "Publish this page?" : "Unpublish this page?",
        text: publish
          ? "Anyone will be able to open /career-fair/" + this.fair.year
          : "The public URL will be taken down.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: publish ? "Publish" : "Unpublish",
      });
      if (!confirm.isConfirmed) return;
      try {
        var result = await this.$apollo.mutate({
          mutation: PUBLISH_MUTATION,
          variables: { id: this.fair.id, publish: publish },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        });
        var payload = result && result.data && result.data.publishCareerFair;
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: payload.message });
        } else {
          this.fair = Object.assign({}, this.fair, { is_published: publish });
        }
      } catch (e) {
        if (this.$swal) this.$swal.fire({ icon: "error", title: "Failed", text: "Please try again." });
      }
    },
  },
};
</script>
